import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { CoreModule } from '@abp/ng.core';
import { SettingManagementConfigModule } from '@abp/ng.setting-management/config';
import {
  ConfirmationService,
  CUSTOM_ERROR_HANDLERS,
  ThemeSharedModule,
  ToasterService,
} from '@abp/ng.theme.shared';
import { APP_INITIALIZER, Injector, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { NgxsModule } from '@ngxs/store';
import { CommercialUiConfigModule } from '@volo/abp.commercial.ng.ui/config';
import { AccountAdminConfigModule } from '@volo/abp.ng.account/admin/config';
import { AccountPublicConfigModule } from '@volo/abp.ng.account/public/config';
import { AuditLoggingConfigModule } from '@volo/abp.ng.audit-logging/config';
import { OpeniddictproConfigModule } from '@volo/abp.ng.openiddictpro/config';
import {
  IdentityConfigModule,
  IdentitySessionsSettingComponent,
} from '@volo/abp.ng.identity/config';
import { LanguageManagementConfigModule } from '@volo/abp.ng.language-management/config';
import { registerLocale } from '@volo/abp.ng.language-management/locale';
import { SaasConfigModule } from '@volo/abp.ng.saas/config';
import { TextTemplateManagementConfigModule } from '@volo/abp.ng.text-template-management/config';
import { provideThemeLepton } from '@volo/abp.ng.theme.lepton';
import { CustomHttpErrorComponent } from './shared/components/custom-http-error/custom-http-error.component';
import { AbpOAuthModule } from '@abp/ng.oauth';
import { PremiumReportingConfigModule } from '@sestek/premium-reporting/config';
import { environment } from '../environments/environment';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { INTEGRATIONS_ROUTE_PROVIDER } from './integrations/providers/integration-route.provider';
import { BotHubSettingsComponent } from './settings/bothub-settings/bothub-settings.component';
import { PROJECTS_PROJECT_ROUTE_PROVIDER } from './projects/providers/project-route.provider';
import { APP_ROUTE_PROVIDER } from './route.provider';
import { CanDeactivateGuard } from './shared/can-deactivate/can-deactivate.guard';
import { SharedModule } from './shared/shared.module';
import { VersionInfoComponent } from './shared/nav-items/version-info/version-info.component';
import { AGENTS_AGENT_ROUTE_PROVIDER } from './virtual-agents/providers/virtual-agent-route.provider';
import { DASHBOARD_ROUTE_PROVIDER } from './dashboard/providers/dashboard-route.provider';
import { CaIntegrationSettingsComponent } from './settings/ca-integration-settings/ca-integration-settings.component';
import {
  AppSettingsService,
  initializeApplicationSettings,
} from './saas-extended/app-settings/app-settings.service';
import { BlockUIModule } from 'ng-block-ui';
import { INSIGHTS_ROUTE_PROVIDER } from './insights/providers/insights-route.provider';
import { NotificationModule } from '@sestek/notification';
import { AppNotificationModule } from './notifications/app-notification.module';
import { FooterComponent } from './shared/footer/footer.component';
import { AIUsageDisclaimerComponent } from './shared/ai-usage-disclaimer/ai-usage-disclaimer.component';
import { AddOnAIFeaturesSettingsComponent } from './settings/addon-ai-features-settings/addon-ai-features-settings.component';
import { NgSelectModule } from '@ng-select/ng-select';
import { VALIDATION_ERROR_TEMPLATE } from '@ngx-validate/core';
import { ErrorComponent } from './shared/form-validator';
import { LogoComponent } from './shared/nav-items/logo/logo.component';
import { NotificationIconComponent } from './shared/nav-items/notification-icon/notification-icon.component';
import { ProjectEditIconComponent } from './shared/nav-items/project-edit-icon/project-edit-icon.component';
import { CurrentUserComponent } from './shared/nav-items/current-user/current-user.component';
import { PROMPTS_ROUTE_PROVIDER } from './prompts/providers/prompts-route.provider';
import { CustomConfirmationService } from './shared/confirmation/custom-confirmation.service';
import { CustomToasterService } from './shared/toaster/custom-toaster.service';
import { ToastrModule } from 'ngx-toastr';
import { MarkdownModule } from 'ngx-markdown';
import { RedactionSettingsComponent } from './settings/redaction-settings/redaction-settings.component';
import { OutboundConversationSettingsComponent } from './settings/outbound-conversation-settings/outbound-conversation-settings.component';
import { CUSTOM_NAV_ITEMS } from './shared/nav-items/nav-items.const';
import { CaiHttpErrorHandlerService } from './shared/services/cai-http-error-handler.service';
import { GenerativeQnaSettingsSettingsComponent } from './settings/generative-qna-settings/generative-qna-settings.component';
import { CustomIdentitySettingsComponent } from './settings/cai-identity-settings/identity-settings.component';
import { IdentityLdapSettingsComponent } from './settings/cai-identity-settings/identity-setting-tabs';
import { AiTestingSettingsComponent } from './settings/ai-testing-settings/ai-testing-settings.component';
import { BrandingSettingsComponent } from './settings/branding-settings/branding-settings.component';
export let APP_INJECTOR: Injector;

@NgModule({
  declarations: [
    AppComponent,
    VersionInfoComponent,
    BotHubSettingsComponent,
    CaIntegrationSettingsComponent,
    AddOnAIFeaturesSettingsComponent,
    CustomIdentitySettingsComponent,
    IdentityLdapSettingsComponent,
    GenerativeQnaSettingsSettingsComponent,
    FooterComponent,
    AIUsageDisclaimerComponent,
    LogoComponent,
    NotificationIconComponent,
    ProjectEditIconComponent,
    CurrentUserComponent,
    ErrorComponent,
    RedactionSettingsComponent,
    OutboundConversationSettingsComponent,
    BrandingSettingsComponent,
    CustomHttpErrorComponent,
    AiTestingSettingsComponent,
  ],
  imports: [
    NgbModule,
    PremiumReportingConfigModule.forRoot(),
    BrowserModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    CoreModule.forRoot({
      environment,
      registerLocaleFn: registerLocale(),
    }),
    ThemeSharedModule.forRoot({
      httpErrorConfig: {
        errorScreen: {
          component: CustomHttpErrorComponent,
          forWhichErrors: [401, 403, 404, 500],
          hideCloseIcon: true,
        },
      },
    }),
    AbpOAuthModule.forRoot(),
    AccountAdminConfigModule.forRoot(),
    AccountPublicConfigModule.forRoot(),
    IdentityConfigModule.forRoot(),
    LanguageManagementConfigModule.forRoot(),
    SaasConfigModule.forRoot(),
    AuditLoggingConfigModule.forRoot(),
    OpeniddictproConfigModule.forRoot(),
    TextTemplateManagementConfigModule.forRoot(),
    SettingManagementConfigModule.forRoot(),
    CommercialUiConfigModule.forRoot(),
    NgxsModule.forRoot([]),
    NotificationModule.forRoot({
      baseUrl: environment.apis.default.url,
      systemDateFormat: 'dd/MM/yyyy',
      showBadge: false,
      playSound: false,
      audioFileUrl: 'assets/sounds/notification-alert.mp3',
    }),
    AppNotificationModule,
    BlockUIModule.forRoot({
      delayStart: 200,
    }),
    SharedModule,
    NgSelectModule,
    ToastrModule.forRoot(),
    MarkdownModule.forRoot(),
    IdentitySessionsSettingComponent,
  ],
  providers: [
    APP_ROUTE_PROVIDER,
    AGENTS_AGENT_ROUTE_PROVIDER,
    PROJECTS_PROJECT_ROUTE_PROVIDER,
    INTEGRATIONS_ROUTE_PROVIDER,
    DASHBOARD_ROUTE_PROVIDER,
    INSIGHTS_ROUTE_PROVIDER,
    PROMPTS_ROUTE_PROVIDER,
    provideThemeLepton(),
    CUSTOM_NAV_ITEMS,
    CanDeactivateGuard,
    {
      provide: APP_INITIALIZER,
      useFactory: initializeApplicationSettings,
      deps: [AppSettingsService],
      multi: true,
    },
    {
      provide: VALIDATION_ERROR_TEMPLATE,
      useValue: ErrorComponent,
    },
    {
      provide: CUSTOM_ERROR_HANDLERS,
      useExisting: CaiHttpErrorHandlerService,
      multi: true,
    },
    {
      provide: ConfirmationService,
      useClass: CustomConfirmationService,
    },
    {
      provide: ToasterService,
      useClass: CustomToasterService,
    },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {
  constructor(private injector: Injector) {
    APP_INJECTOR = this.injector;
  }
}
